import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import FieldsetContainer from 'components/blocks/Delta/Inputs/Containers/FieldsetContainer';
import Loader from 'components/blocks/Loader/Loader';
import AccordionForm from 'components/blocks/ManagementNewForms/Accordion/AccordionForm';
import FormInputCuentaBancaria from 'components/blocks/ManagementNewForms/Inputs/FormInputCuentaBancaria';
import FormInputText from 'components/blocks/ManagementNewForms/Inputs/FormInputTexto';
import ErrorDisplay from 'components/blocks/ManagementNewForms/ErrorManagement/ErrorPantalla';
import FormObservaciones from 'components/blocks/ManagementNewForms/Inputs/FormObservaciones';
import FormBotonSubmit from 'components/blocks/ManagementNewForms/Inputs/FormInputSubmit';
import FormDatosPersonales from 'components/blocks/ManagementNewForms/ButtonGroups/FormDatosPersonales';
import FormArrayArchivosReducidos, { adaptDocumentacion, prepareDocumentacionArrayWithCase } from 'components/blocks/ManagementNewForms/ButtonGroups/ArrayInputArchivo';
import FormInputDropDown from 'components/blocks/ManagementNewForms/Inputs/FormInputDropDown';
import FormInfoCreateUpdate from 'components/blocks/ManagementNewForms/Inputs/FormInfoCreateUpdate';
import useDecode from 'hooks/useDecode';
import useCreateSolicitudGastosFarmacia from 'services/record/SolicitudGastosFarmacia/useCreateSolicitudGastosFarmacia';
import useUpdateSolicitudGastosFarmaciaByID from 'services/record/SolicitudGastosFarmacia/useUpdateSolicitudGastosFarmacia';

interface SolicitudGastosFarmaciaProps {
    info: boolean;
    dataUser: any;
    onSuccess?: () => void;
}

const MAX_SIZE_MB = 30;

const SolicitudGastosFarmacia: React.FC<SolicitudGastosFarmaciaProps> = ({ info, dataUser, onSuccess }) => {
    const formMethods = useForm();
    const { register, handleSubmit, reset } = formMethods;
    const tokenResponse = useDecode("token");

    const { createSolicitudGastosFarmacia, loadingCreateSolicitudGastosFarmacia, successCreateSolicitudGastosFarmacia, errorCreateSolicitudGastosFarmacia } = useCreateSolicitudGastosFarmacia();
    const { updateSolicitudGastosFarmaciaById, loadingUpdateSolicitudGastosFarmaciaById, successUpdateSolicitudGastosFarmaciaById, errorUpdateSolicitudGastosFarmaciaById } = useUpdateSolicitudGastosFarmaciaByID();

    const [totalSize, setTotalSize] = useState(0);
    const [datosAPI, setDatosAPI] = useState(dataUser);
    const [expedienteArray, setExpedienteArray] = useState([]);
    const [loader, setLoader] = useState(false);
    const [errorObservaciones, setErrorObservaciones] = useState(false);
    const [bloquearEnvio, setBloquearEnvio] = useState<boolean>(false);
    const [expedienteBloqueadoLabel, setExpedienteBloqueadoLabel] = useState("");
    const [expedienteNoEncontradoError, setExpedienteNoEncontradoError] = useState<string | null>(null);

    const getValue = (...values: any[]) => values.find(value => value !== undefined && value !== null);
    const parseNumberOrNull = (value: any) => {
        if (value === null || value === undefined || value === "") return null;
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
    };
    const parseDecimalOrNull = (value: any) => {
        if (value === null || value === undefined || value === "") return null;
        const parsed = Number(String(value).replace(',', '.'));
        return Number.isNaN(parsed) ? null : parsed;
    };
    const formatIban = (value: any) => {
        if (value === null || value === undefined || value === "") return null;
        return String(value).replace(/(.{4})(.{4})(.{4})(.{2})/, '$1-$2-$3-$4-');
    };
    const getPrestacionesCP = (data: any) => {
        const prestaciones = getValue(data?.PrestacionCP, data?.prestacionCP, []);
        return Array.isArray(prestaciones) ? prestaciones : [];
    };
    const getExpedienteExternalId = (item: any) => getValue(item?.IdExternalPrestacion, item?.ExpedienteId, item?.expedienteId, null);
    const getPrestacionId = (item: any) => getValue(item?.IdPortalPrestacion, item?.IdPrestacion, item?.PrestacionId, item?.prestacionId, null);
    const getExpedienteLabel = (item: any) => `${getValue(item?.CodigoExpediente, item?.codigoExpediente, '')} - ${getExpedienteExternalId(item) ?? ''}`;
    const adaptDocumentacionRequerida = (seccionesDocumentacion: any[]) =>
        adaptDocumentacion(seccionesDocumentacion || []).map((doc) => ({
            ...doc,
            Obligatorio: true,
        }));

    const adaptApiResponseToDefaultValues = (data: any) => {
        if (!data) {
            return;
        }

        const prestacionesCP = getPrestacionesCP(data);
        const expedienteIdByOrigen = getValue(data?.ExpedienteIdByOrigen, data?.expedienteIdByOrigen, null);
        const lockExpedienteByOrigenActivo = info === true && expedienteIdByOrigen !== null && expedienteIdByOrigen !== undefined && expedienteIdByOrigen !== "";
        let expedienteBloqueado = null;

        if (prestacionesCP.length > 0) {
            const expedientesAdaptados = prestacionesCP.map(item => ({
                titulo: getExpedienteLabel(item),
                value: getPrestacionId(item),
            }));

            setExpedienteArray(expedientesAdaptados);

            if (lockExpedienteByOrigenActivo) {
                expedienteBloqueado = prestacionesCP.find(
                    item => String(getExpedienteExternalId(item) ?? "") === String(expedienteIdByOrigen)
                ) || null;
            }
        } else {
            setExpedienteArray([]);
        }

        if (lockExpedienteByOrigenActivo && expedienteBloqueado) {
            setExpedienteBloqueadoLabel(getExpedienteLabel(expedienteBloqueado));
            setExpedienteNoEncontradoError(null);
        } else if (lockExpedienteByOrigenActivo) {
            setExpedienteBloqueadoLabel("");
            setExpedienteNoEncontradoError(`No se encuentra el ExpedienteId ${String(expedienteIdByOrigen)} entre los Expedientes del Usuario.`);
            return;
        } else {
            setExpedienteBloqueadoLabel("");
            setExpedienteNoEncontradoError(null);
        }

        const datosPersonales = getValue(data?.DatosPersonales, data?.datosPersonales, {});
        const datosBancarios = getValue(data?.DatosBancarios, data?.datosBancarios, {});
        const rawIban = getValue(datosBancarios?.SolicitanteIBAN, datosBancarios?.solicitanteIBAN, data?.IBAN, data?.iban, data?.NumeroCuentaConIBAN, data?.numeroCuentaConIBAN, null);
        const estadoFormulario = getValue(data?.EstadoFormulario, data?.estadoFormulario, null);
        const seccionesDocumentacion = getValue(data?.SeccionesDocumentacion, data?.seccionesDocumentacion, data?.Documentacion, data?.documentacion, []);

        setErrorObservaciones(estadoFormulario === 2);

        reset({
            DatosPersonales: {
                SolicitanteNombre: getValue(datosPersonales?.Nombre, datosPersonales?.nombre, null),
                SolicitanteApellido1: getValue(datosPersonales?.Apellido1, datosPersonales?.apellido1, null),
                SolicitanteApellido2: getValue(datosPersonales?.Apellido2, datosPersonales?.apellido2, null),
                SolicitanteIPF: getValue(datosPersonales?.IPF, datosPersonales?.ipf, null),
                SolicitanteIPFTipo: getValue(datosPersonales?.IPFTipo, datosPersonales?.ipfTipo, null)?.toString() ?? null,
                SolicitanteTelefono: getValue(datosPersonales?.Telefono, datosPersonales?.telefono, null),
                SolicitanteFijo: getValue(datosPersonales?.Fijo, datosPersonales?.fijo, null),
                SolicitanteDomicilio: getValue(datosPersonales?.Domicilio, datosPersonales?.domicilio, null),
                SolicitanteCodigoPostal: getValue(datosPersonales?.CodigoPostal, datosPersonales?.codigoPostal, null),
                SolicitanteProvincia: getValue(datosPersonales?.CodigoProvincia, datosPersonales?.codigoProvincia, null),
                SolicitanteProvinciaDescripcion: getValue(datosPersonales?.ProvinciaDescripcion, datosPersonales?.provinciaDescripcion, datosPersonales?.Provincia, null),
                SolicitanteComunidadCodigo: getValue(datosPersonales?.ComunidadCodigo, datosPersonales?.CodigoLocalidad, datosPersonales?.codigoLocalidad, null),
                SolicitanteComunidadCodigoDescripcion: getValue(datosPersonales?.ComunidadCodigoDescripcion, datosPersonales?.LocalidadDescripcion, datosPersonales?.localidadDescripcion, null),
            },
            TipoGastos: {
                Importe: info && Number(getValue(data?.Importe, data?.importe, null)) === 0
                    ? null
                    : getValue(data?.Importe, data?.importe, null),
                ExpedienteGasto: info
                    ? (lockExpedienteByOrigenActivo ? (getPrestacionId(expedienteBloqueado) ?? null) : null)
                    : `${getValue(data?.CodigoExpediente, data?.codigoExpediente, '')} - ${getValue(data?.ExpedienteId, data?.expedienteId, '')}`,
                Documentacion: adaptDocumentacionRequerida(seccionesDocumentacion),
            },
            DatosBancarios: {
                SolicitanteIBAN: rawIban ? String(rawIban).replace(/-/g, '') : null,
            },
            Acepto: getValue(data?.Acepto, data?.acepto, null),
        });
    };

    useEffect(() => {
        setDatosAPI(dataUser);
        adaptApiResponseToDefaultValues(dataUser);
    }, [dataUser]);

    useEffect(() => {
        setBloquearEnvio(totalSize > MAX_SIZE_MB || Boolean(expedienteNoEncontradoError));
    }, [totalSize, expedienteNoEncontradoError]);

    const onSubmit = async (data: any) => {
        setLoader(true);

        if (expedienteNoEncontradoError) {
            setLoader(false);
            return;
        }

        const prestacionesCP = getPrestacionesCP(datosAPI);
        const expedienteIdByOrigen = getValue(datosAPI?.ExpedienteIdByOrigen, datosAPI?.expedienteIdByOrigen, null);
        const lockExpedienteByOrigenActivo = info === true && expedienteIdByOrigen !== null && expedienteIdByOrigen !== undefined && expedienteIdByOrigen !== "";
        const expedienteSeleccionado = lockExpedienteByOrigenActivo
            ? prestacionesCP.find(item => String(getExpedienteExternalId(item) ?? "") === String(expedienteIdByOrigen))
            : prestacionesCP.find(item => String(getPrestacionId(item) ?? "") === String(data?.TipoGastos?.ExpedienteGasto ?? ""));

        const expedienteIdToSend = info
            ? getValue(getExpedienteExternalId(expedienteSeleccionado), datosAPI?.ExpedienteId, datosAPI?.expedienteId, null)
            : getValue(datosAPI?.ExpedienteId, datosAPI?.expedienteId, null);
        const prestacionIdToSend = info
            ? getValue(getPrestacionId(expedienteSeleccionado), datosAPI?.PrestacionId, datosAPI?.prestacionId, null)
            : getValue(datosAPI?.PrestacionId, datosAPI?.prestacionId, null);
        const documentacionArray = await prepareDocumentacionArrayWithCase(data?.TipoGastos?.Documentacion ?? []);
        const datosPersonalesAPI = getValue(datosAPI?.DatosPersonales, datosAPI?.datosPersonales, {});

        const datosEnvio = {
            Id: datosAPI?.Id ?? null,
            PersonaId: parseInt(tokenResponse?.extension_external_id ?? "0"),
            UsuarioId: tokenResponse?.extension_id ?? null,
            ImpersonadorId: datosAPI?.ImpersonadorId ?? null,
            Acepto: data?.Acepto ?? false,
            ExpedienteId: expedienteIdToSend,
            PrestacionId: prestacionIdToSend,
            DatosPersonales: {
                Nombre: data?.DatosPersonales?.SolicitanteNombre ?? null,
                Apellido1: data?.DatosPersonales?.SolicitanteApellido1 ?? null,
                Apellido2: data?.DatosPersonales?.SolicitanteApellido2 ?? null,
                IPF: data?.DatosPersonales?.SolicitanteIPF ?? null,
                IPFTipo: parseNumberOrNull(data?.DatosPersonales?.SolicitanteIPFTipo),
                Telefono: data?.DatosPersonales?.SolicitanteTelefono ?? null,
                Fijo: data?.DatosPersonales?.SolicitanteFijo ?? null,
                Domicilio: data?.DatosPersonales?.SolicitanteDomicilio ?? null,
                CodigoPostal: data?.DatosPersonales?.SolicitanteCodigoPostal ?? null,
                ProvinciaDescripcion: data?.DatosPersonales?.SolicitanteProvinciaDescripcion ?? null,
                CodigoProvincia: data?.DatosPersonales?.SolicitanteProvincia ?? null,
                CodigoLocalidad: data?.DatosPersonales?.SolicitanteComunidadCodigo ?? null,
                LocalidadDescripcion: data?.DatosPersonales?.SolicitanteComunidadCodigoDescripcion ?? null,
                NumeroSeguridadSocial: getValue(datosPersonalesAPI?.NumeroSeguridadSocial, datosPersonalesAPI?.numeroSeguridadSocial, null),
            },
            CodigoExpediente: info
                ? getValue(expedienteSeleccionado?.CodigoExpediente, expedienteSeleccionado?.codigoExpediente, null)
                : getValue(datosAPI?.CodigoExpediente, datosAPI?.codigoExpediente, null),
            Importe: parseDecimalOrNull(data?.TipoGastos?.Importe),
            IBAN: formatIban(data?.DatosBancarios?.SolicitanteIBAN),
            Documentacion: documentacionArray,
        };

        try {
            const userId = tokenResponse?.extension_id ?? datosAPI?.UsuarioId ?? "";

            if (info) {
                await createSolicitudGastosFarmacia(userId, datosEnvio, onSuccess);
            } else {
                await updateSolicitudGastosFarmaciaById(userId, String(datosAPI?.Id ?? ""), datosEnvio, onSuccess);
            }

            setBloquearEnvio(true);
        } catch (error) {
            console.error("Error al enviar SolicitudGastosFarmacia:", error);
        } finally {
            setTimeout(() => {
                setLoader(false);
            }, 500);
        }
    };

    return (
        <>
            <Loader activeLoader={loader || loadingCreateSolicitudGastosFarmacia || loadingUpdateSolicitudGastosFarmaciaById} label="Cargando Formulario" />

            {expedienteNoEncontradoError ? (
                <ErrorDisplay descripcionError={expedienteNoEncontradoError} />
            ) : (
                <FormProvider {...formMethods}>
                    <div className="container form--simple-styled" id="formDeltawithoutleave">
                        <FormObservaciones observaciones={datosAPI?.Observaciones} titulo={'Observaciones del Gestor a la solicitud'} errorColor={errorObservaciones} />

                        <FormInfoCreateUpdate
                            isCreate={info}
                            estadoFormulario={datosAPI?.EstadoFormulario ?? datosAPI?.estadoFormulario}
                            estadoFormularioDescripcion={datosAPI?.EstadoFormularioDescripcion ?? datosAPI?.estadoFormularioDescripcion}
                            fechaActualizacionEstado={datosAPI?.FechaActualizacionEstado ?? datosAPI?.fechaActualizacionEstado}
                        />

                        <FormDatosPersonales />

                        <AccordionForm title={'Datos del Gasto'} expandedInit={false} nombreError='TipoGastos' totalSize={totalSize} maxSize={MAX_SIZE_MB} >
                            <div className="row">
                                <div className="col-xl-12">
                                    <FieldsetContainer legend="Importe y Prestación relacionada" disabled={false}>
                                        <FormInputText
                                            name='TipoGastos.Importe'
                                            id='TipoGastos.Importe'
                                            label='Importe'
                                            md={4}
                                            xl={4}
                                            pattern={/^(?:[1-9][0-9]*(?:[.,][0-9]+)?|0[.,][0-9]*[1-9][0-9]*)$/}
                                            patternMessage='Introduce un importe mayor que 0 usando numeros enteros o decimales con "." o ","'
                                            modal='El Importe debe corresponder a la suma de los gastos que adjunta y justifica en los tickets'
                                            req='El Importe es Obligatorio'
                                        />
                                        {info ?
                                            (expedienteBloqueadoLabel ?
                                                <>
                                                    <input type='hidden' {...register('TipoGastos.ExpedienteGasto')} />
                                                    <FormInputText
                                                        key={expedienteBloqueadoLabel}
                                                        name='TipoGastos.ExpedienteGastoBloqueado'
                                                        id='TipoGastos.ExpedienteGastoBloqueado'
                                                        label='Prestación relacionada al gasto'
                                                        md={8}
                                                        xl={8}
                                                        disabled={true}
                                                        defaultValue={expedienteBloqueadoLabel}
                                                    />
                                                </>
                                                :
                                                <FormInputDropDown
                                                    name="TipoGastos.ExpedienteGasto"
                                                    label="Prestación relacionada al gasto"
                                                    required="Debe seleccionar una Prestacion"
                                                    options={expedienteArray}
                                                    xl={8}
                                                    md={8}
                                                    col={12}
                                                    placeholder='Selecciona un expediente'
                                                />)
                                            :
                                            <FormInputText
                                                name='TipoGastos.ExpedienteGasto'
                                                id='TipoGastos.ExpedienteGasto'
                                                label='Prestación relacionada al gasto'
                                                md={8}
                                                xl={8}
                                                disabled={true}
                                            />
                                        }

                                    </FieldsetContainer>
                                    <FormArrayArchivosReducidos
                                        name={'TipoGastos.Documentacion'}
                                        urlArchivos={datosAPI?.Documentacion?.map(d => d?.SasUri) || []}
                                        setTotalSize={setTotalSize}
                                    />
                                </div>
                            </div>
                        </AccordionForm>

                        <AccordionForm title={'Datos de Abono'} expandedInit={false} nombreError='DatosBancarios' >
                            <div className="row mb-4">
                                <div className="col-xl-12">
                                    <div className="row mb-4">
                                        <div className="col-xl-12">
                                            <FormInputCuentaBancaria
                                                id='DatosBancarios.SolicitanteIBAN'
                                                name='DatosBancarios.SolicitanteIBAN'
                                                xl={12}
                                                md={12}
                                                placeholder=''
                                                req='Falta el Número de Cuenta Bancaria'
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </AccordionForm>

                        <FormBotonSubmit
                            disableSubmit={bloquearEnvio}
                            onSubmit={handleSubmit(onSubmit)}
                            successCreate={successCreateSolicitudGastosFarmacia}
                            successUpdate={successUpdateSolicitudGastosFarmaciaById}
                            errorCreate={errorCreateSolicitudGastosFarmacia}
                            errorUpdate={errorUpdateSolicitudGastosFarmaciaById}
                            datosAPI={datosAPI}
                            info={info}
                            BloqueoForm={[1, 3, 4, 5]}
                            MaxSizeMB={MAX_SIZE_MB}
                        />

                        <div className='paragraph text-info mt-5'>Solo se abonarán gastos que estén debidamente justificados y autorizados por
                            Solimat Mutua Colaboradora con la Seguridad Social nº 72.</div>
                        <br />
                        <br />
                        <p style={{ fontSize: '12px' }} className='paragraph text-secondary mb-4'>En cumplimiento de lo establecido en la normativa sobre de Protección de Datos de Carácter Personal, Solimat Mutua Colaboradora con la Seguridad Social nº 72 le informa que sus datos de
                            carácter personal serán objeto de tratamiento, con la finalidad del mantenimiento y cumplimiento de la relación. La base jurídica que legitima el tratamiento de sus datos es el
                            cumplimiento de una obligación legal. Sus datos serán conservados de forma indefinida en nuestra entidad para atender a las posibles responsabilidades derivadas del tratamiento. Usted
                            podrá revocar el consentimiento prestado, así como ejercitar sus derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento, o portabilidad, en su caso,
                            remitiendo un escrito a la siguiente dirección: Solimat, Mutua Colaboradora con la Seguridad Social nº 72, C/ Berna, 1, 45003, Toledo, adjuntando su NIF. Si considera que sus
                            derechos no han sido debidamente atendidos, puede contactar con nuestro delegado de protección de datos lopd@solimat.com o interponer una reclamación frente a la Agencia
                            Española de Protección de Datos.</p>
                    </div>
                </FormProvider>
            )}
        </>
    );
};

export default SolicitudGastosFarmacia;
